import { environment } from 'src/environments/environment';
import {
  NgModule,
  APP_INITIALIZER,
  ErrorHandler,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  WHAuthHttpINTERCEPTOR,
  WHCoreModule,
  WHLayoutMenunavModule,
  WHMainLayoutModule,
  WHMatPaginatorItilService,
  provideWHBreadcrumbs,
} from '@workheld/workheld-shared-lib';
import { FLOW_BREADCRUMB_CONFIG } from './app-shared/config/breadcrumb.config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppLayoutComponent } from './app-layout.component';
import { WHContentPageModule } from './w-h-content-page/w-h-content-page.module';
import { GoogleMapsModule } from '@angular/google-maps';
import { AppInitializerService } from './app-initializer.service';
import { SERVICES_CORE, SERVICES_DATA } from './app.index.services';

import { SERVICES_HELPER } from './app-services-helper/index.helper.services';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { dialogModules, SERVICES_DIALOG } from './app-dialogs/app.index.dialog';
import { StoreModule } from '@ngrx/store';
import { reducers, metaReducers } from './app-stores/index.stores';
import {
  StoreDevtoolsModule,
  StoreDevtoolsOptions,
} from '@ngrx/store-devtools';
import { IndexStoresModule } from './app-stores/index-stores.module';
import {
  HttpClientModule,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { FormReferenceService } from './app-services-helper/form-reference.service';
import { AppSharedModule } from './app-shared/app-shared.module';
import { Router, RouterModule } from '@angular/router';
import {
  MatFormFieldDefaultOptions,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import { EffectsModule } from '@ngrx/effects';
import { AppEffects } from './app.effects';

import * as Sentry from '@sentry/angular';
import { ErrorHandlerService } from './app-services-async/w-h-error-handler.service';
import { NULL_URL_INTERCEPTOR } from './app-services/null-url.interceptor';
import { PICTURE_CACHE_INTERCEPTOR } from './app-services/picture-cache.interceptor';
import { WHAiButtonComponent } from './app-modules-pages/workheld-ai/components/wh-ai-button.component';
import { WorkheldAiComponent } from './app-modules-pages/workheld-ai/workheld-ai.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ToastContainerComponent } from './app-shared/toast-container/toast-container.component';

function initializeApp(appInitializerService: AppInitializerService): any {
  return (): Promise<any> => {
    return appInitializerService.initializeApp();
  };
}

const appearance: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  subscriptSizing: 'dynamic',
};

@NgModule({
  declarations: [AppComponent, AppLayoutComponent],
  imports: [
    ...dialogModules,
    WHCoreModule,
    MatMomentDateModule,
    // No serverLoggingUrl: flowservice has no POST /logging handler (404), and
    // ngx-logger rethrows the failed upload as an unhandled error.
    LoggerModule.forRoot({
      level: NgxLoggerLevel.TRACE,
      disableConsoleLogging: false,
      enableSourceMaps: true,
    }),
    WHLayoutMenunavModule,
    GoogleMapsModule,
    IndexStoresModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    EffectsModule.forRoot([AppEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    } as StoreDevtoolsOptions),
    WHMainLayoutModule,
    WHContentPageModule,
    AppRoutingModule,
    AppSharedModule,
    RouterModule,
    HttpClientModule,
    WHAiButtonComponent,
    WorkheldAiComponent,
    MatSidenavModule,
    ToastContainerComponent,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideWHBreadcrumbs(FLOW_BREADCRUMB_CONFIG),
    provideZoneChangeDetection(),
    { provide: Window, useValue: window },
    AppInitializerService,
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitializerService, Sentry.TraceService],
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: appearance,
    },
    {
      provide: MatPaginatorIntl,
      useClass: WHMatPaginatorItilService,
    },
    SERVICES_CORE,
    SERVICES_HELPER,
    SERVICES_DATA,
    SERVICES_DIALOG,
    WHAuthHttpINTERCEPTOR,
    NULL_URL_INTERCEPTOR,
    PICTURE_CACHE_INTERCEPTOR,
    FormReferenceService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
